import { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui-kit/button';
import { useContactTimeLines } from '../../hooks/use-contact-timelines';
import { useCustomFieldDefinitions } from '../../hooks/use-custom-field-definitions';
import {
  formatActivitySourceLabel,
  formatContactActivityLabel,
  getContactActivityChangeLines,
  getContactActivityVisual,
} from '../../utils/contact-activity.utils';

const formatActivityTimestamp = (value?: string) => {
  if (!value) return undefined;

  try {
    const date = parseISO(value);
    if (Number.isNaN(date.getTime())) return value;
    return format(date, 'MMM d, yyyy, h:mm a');
  } catch {
    return value;
  }
};

type ContactActivityProps = {
  contactId: string;
};

export const ContactActivity = ({ contactId }: ContactActivityProps) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useContactTimeLines(contactId);
  const activities = (data?.pages ?? []).flatMap((page) => page.items);

  // Custom-field changes arrive keyed by attribute key; show each by its label.
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const attributeLabels = useMemo(
    () =>
      Object.fromEntries(
        (customFieldDefinitionsData?.items ?? [])
          .filter((definition) => definition.Key)
          .map((definition) => [definition.Key, definition.Label || definition.Key])
      ),
    [customFieldDefinitionsData?.items]
  );

  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Activity
        </div>
        <p className="py-8 text-center text-xs text-neutral-500">Loading activity...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Activity
        </div>
        <div className="py-8 text-center">
          <h2 className="text-lg font-semibold text-neutral-900">Unable to load activity</h2>
          <p className="mt-1 text-xs text-neutral-500">
            {error instanceof Error ? error.message : 'Something went wrong while fetching activity.'}
          </p>
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Activity
        </div>
        <p className="py-8 text-center text-xs text-neutral-500">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
      <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Activity
      </div>

      <div className="flex flex-col">
        {activities.map((event) => {
          const { Icon, iconBackground, iconColor } = getContactActivityVisual(event);
          const timestamp = formatActivityTimestamp(event.timestamp);
          const source = formatActivitySourceLabel(event.source, event.sourceClient);
          const meta = [timestamp, source].filter(Boolean).join(' · ');
          const changeLines = getContactActivityChangeLines(event, attributeLabels);

          return (
            <div
              key={event.itemId}
              className="flex gap-3 border-b border-neutral-100 py-3 last:border-b-0"
            >
              <span
                className={`grid size-8 shrink-0 place-items-center rounded-full ${iconBackground}`}
              >
                <Icon className={`size-4 ${iconColor}`} weight="regular" aria-hidden />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm text-neutral-900">{formatContactActivityLabel(event)}</p>
                {changeLines.length > 0 ? (
                  <ul className="flex flex-col gap-0.5 text-xs text-neutral-700">
                    {changeLines.map((line) => (
                      <li key={line} className="break-words">
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {meta ? <p className="text-xs text-neutral-500">{meta}</p> : null}
              </div>
            </div>
          );
        })}
      </div>

      {hasNextPage ? (
        <div className="pt-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 border-neutral-200 bg-neutral-100 text-neutral-900 hover:bg-neutral-200"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </section>
  );
};
